import { fetchApiData, interpolate } from '../../Utils/ApiCalls';
import { formatLocaleDate } from '../../Utils/HelperFunctions';
import {
  COMPANIES_ENDPOINT,
  COMPANY_LOOKUP_ENDPOINT,
  COMPANY_SCOPES,
  SALES_TEAM_MEMBERS_ENDPOINT,
  SCOPE_RECORDS_ENDPOINT,
  SCOPE_RECORD_DETAIL_ENDPOINT,
  SCOPE_RECORD_LINK_COMPANY_ENDPOINT,
  SCOPE_RECORD_LINK_ENDPOINT,
  SCOPE_RECORD_RESEND_INVITE_ENDPOINT,
  SCOPE_RECORD_UNLINK_COMPANY_ENDPOINT
} from '../../urls';
import { fetchScopeCatalog, scopeHasSelectableElements } from '../../Utils/scopeCatalog';

export { fetchScopeCatalog, scopeHasSelectableElements };
export const dateShort = iso => formatLocaleDate(iso, { withYear: false });

export const dateTimeShort = iso => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
};

export const OUTCOME_LABELS = {
  awaiting_adoption: 'Awaiting adoption',
  live: 'Live',
  open: 'Pilot · open',
  closed_won: 'Pilot · closed won',
  closed_lost: 'Pilot · closed lost'
};

export const recordTypeLabel = type =>
  type === 'existing_customer' ? 'Existing customer' : 'Pilot';

export const formatOutcomeLabel = ({ outcome, isPilot, pilotStatus } = {}) => {
  if (isPilot) {
    if (pilotStatus === 'open') return OUTCOME_LABELS.open;
    if (pilotStatus === 'closed_won' || pilotStatus === 'closed') return OUTCOME_LABELS.closed_won;
    if (pilotStatus === 'closed_lost') return OUTCOME_LABELS.closed_lost;
    return '—';
  }
  if (outcome === 'live') return OUTCOME_LABELS.live;
  return OUTCOME_LABELS.awaiting_adoption;
};

const stripEmpty = obj =>
  Object.entries(obj).reduce((a, [k, v]) => {
    if (v === undefined || v === null || v === '') return a;
    a[k] = v;
    return a;
  }, {});

export const fetchScopeRecords = async ({
  linked,
  q,
  scope_form_status,
  internal_user,
  window,
  outcome,
  record_type,
  page = 1,
  limit,
  signal
} = {}) =>
  fetchApiData({
    endpoint: SCOPE_RECORDS_ENDPOINT,
    apiRequest: 'GET',
    params: stripEmpty({
      linked: linked === undefined ? undefined : String(linked),
      q,
      scope_form_status: scope_form_status ? String(scope_form_status).toLowerCase() : undefined,
      internal_user,
      window,
      outcome,
      record_type,
      page,
      limit
    }),
    signal
  });

export const fetchScopeRecord = async (id, { signal } = {}) =>
  fetchApiData({
    endpoint: interpolate(SCOPE_RECORD_DETAIL_ENDPOINT, [id]),
    apiRequest: 'GET',
    signal
  });

export const createIntakeRecord = async body =>
  fetchApiData({
    endpoint: SCOPE_RECORDS_ENDPOINT,
    apiRequest: 'POST',
    data: body
  });

export const patchScopeRecord = async (id, body) =>
  fetchApiData({
    endpoint: interpolate(SCOPE_RECORD_DETAIL_ENDPOINT, [id]),
    apiRequest: 'PATCH',
    data: body
  });

export const linkScopeRecord = async (id, companyId) =>
  fetchApiData({
    endpoint: interpolate(SCOPE_RECORD_LINK_ENDPOINT, [id]),
    apiRequest: 'POST',
    data: { company_id: companyId }
  });

export const linkCompanyToScopeRecord = async ({ scopeRecordId, companyId, cardId, reason } = {}) =>
  fetchApiData({
    endpoint: interpolate(SCOPE_RECORD_LINK_COMPANY_ENDPOINT, [scopeRecordId || cardId]),
    apiRequest: 'POST',
    data: stripEmpty({ company_id: companyId, card_id: cardId, reason })
  });

export const unlinkCompanyFromScopeRecord = async ({ scopeRecordId, reason } = {}) =>
  fetchApiData({
    endpoint: interpolate(SCOPE_RECORD_UNLINK_COMPANY_ENDPOINT, [scopeRecordId]),
    apiRequest: 'POST',
    data: stripEmpty({ reason })
  });

export const resendScopeInvite = async id =>
  fetchApiData({
    endpoint: interpolate(SCOPE_RECORD_RESEND_INVITE_ENDPOINT, [id]),
    apiRequest: 'POST'
  });

let salesTeamMembersPromise = null;

/** Cached for the session — AE/FDE list is static and fetched from several Scope Console screens. */
export const fetchSalesTeamMembers = async () => {
  if (!salesTeamMembersPromise) {
    salesTeamMembersPromise = fetchApiData({
      endpoint: SALES_TEAM_MEMBERS_ENDPOINT,
      apiRequest: 'GET'
    }).then(result => {
      if (result?.error) salesTeamMembersPromise = null;
      return result;
    });
  }
  return salesTeamMembersPromise;
};

export const fetchCompanyLookup = async ({ email, signal } = {}) =>
  fetchApiData({
    endpoint: COMPANY_LOOKUP_ENDPOINT,
    apiRequest: 'GET',
    params: stripEmpty({ email }),
    signal
  });

/** List registered companies for existing-customer intake. */
export const fetchCompanies = async ({ q, signal, page = 1, limit = 50 } = {}) =>
  fetchApiData({
    endpoint: COMPANIES_ENDPOINT,
    apiRequest: 'GET',
    params: stripEmpty({ q, page, limit }),
    signal
  });

/** Normalize a person / id / email field from company payloads into { id, name, email }. */
export const coercePersonRef = value => {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) {
    for (const entry of value) {
      const person = coercePersonRef(entry);
      if (person) return person;
    }
    return null;
  }
  if (typeof value === 'object') {
    const id = value.id ?? value.user_id ?? value.pk ?? value.uuid ?? null;
    const email = value.email || value.user_email || '';
    const name = value.name || value.user_name || value.full_name || '';
    if (id == null || id === '') {
      return email ? { id: null, name, email } : null;
    }
    return { id: String(id), name, email };
  }
  const raw = String(value).trim();
  if (!raw) return null;
  if (raw.includes('@')) return { id: null, name: '', email: raw };
  return { id: raw, name: '', email: '' };
};

/** Resolve a company owner onto a roster Select value (id match, then email). */
export const resolveRosterValue = (person, roster) => {
  if (!person) return '';
  const list = roster || [];
  if (person.id) {
    const byId = list.find(u => String(u.value) === String(person.id));
    if (byId) return String(byId.value);
  }
  if (person.email) {
    const want = person.email.trim().toLowerCase();
    const byEmail = list.find(u => (u.email || '').trim().toLowerCase() === want);
    if (byEmail) return String(byEmail.value);
  }
  return person.id ? String(person.id) : '';
};

export const mapCompanyOption = raw => {
  const kam =
    coercePersonRef(raw.key_account_manager) ||
    coercePersonRef(raw.key_account_managers) ||
    coercePersonRef(raw.kam) ||
    coercePersonRef(raw.account_owner) ||
    coercePersonRef(raw.key_account_manager_id) ||
    coercePersonRef(raw.kam_id) ||
    coercePersonRef(raw.account_owner_id);
  const eam =
    coercePersonRef(raw.enterprise_account_manager) ||
    coercePersonRef(raw.enterprise_account_managers) ||
    coercePersonRef(raw.eam) ||
    coercePersonRef(raw.enterprise_account_manager_id) ||
    coercePersonRef(raw.eam_id);
  const tcsm =
    coercePersonRef(raw.technical_csm) ||
    coercePersonRef(raw.technical_owner) ||
    coercePersonRef(raw.technical_csm_id) ||
    coercePersonRef(raw.technical_owner_id) ||
    coercePersonRef(raw.tcsm_id);

  // Prefill: company's KAM, or its EAM when it has no KAM
  const accountOwner = kam || eam;

  return {
    id: raw.id,
    name: raw.name || '—',
    adminEmail: raw.admin_email || raw.customer_email || '',
    accountOwnerId: accountOwner?.id || null,
    accountOwnerName: accountOwner?.name || '',
    accountOwnerEmail: accountOwner?.email || '',
    accountOwnerRoleTag: kam ? 'KAM' : eam ? 'EAM' : '',
    accountOwnerPerson: accountOwner,
    technicalOwnerId: tcsm?.id || null,
    technicalOwnerName: tcsm?.name || '',
    technicalOwnerEmail: tcsm?.email || '',
    technicalOwnerPerson: tcsm
  };
};

export const submitCompanyScopes = async ({
  scope_ids,
  element_ids,
  modifier_answers,
  notes_data,
  internal_note,
  card_id,
  actor_email
} = {}) =>
  fetchApiData({
    endpoint: COMPANY_SCOPES,
    apiRequest: 'POST',
    data: stripEmpty({
      scope_ids,
      element_ids,
      modifier_answers,
      notes_data,
      internal_note,
      card_id,
      actor_email
    })
  });

export const STATE_UI = {
  NEW: 'New',
  SENT: 'Sent',
  SUBMITTED: 'Submitted',
  REOPENED: 'Reopened',
  RESUBMITTED: 'Submitted',
  SENT_LINK_EXPIRED: 'Sent',
  REOPENED_LINK_EXPIRED: 'Reopened'
};

/** Form/intake state without link expiry folded in (item 33). */
export const intakeStateFromStatus = status => {
  const key = String(status || '').toUpperCase();
  return STATE_UI[key] || 'Sent';
};

/** Link chip: Not sent | Sent | Expired (item 33). Blank when submitted (link expired on submit). */
export const linkStatusFromRecord = raw => {
  const key = String(raw?.scope_form_status || raw?.state || '').toUpperCase();
  const linkRaw = String(raw?.link_status || '').toLowerCase();
  const onSubmit = key === 'SUBMITTED' || key === 'RESUBMITTED';

  if (linkRaw === 'not_sent') return { status: 'Not sent', hover: '' };
  if (linkRaw === 'expired') {
    // Submitted records expire the link on submit — don't show Expired in the Link column.
    if (onSubmit) return { status: '', hover: '' };
    return {
      status: 'Expired',
      hover: raw?.link_expires_at ? `Expired ${dateShort(raw.link_expires_at)}` : ''
    };
  }
  if (linkRaw === 'sent') {
    return {
      status: 'Sent',
      hover: raw?.link_expires_at ? `Valid until ${dateShort(raw.link_expires_at)}` : ''
    };
  }

  if (key === 'NEW') return { status: 'Not sent', hover: '' };
  if (onSubmit) return { status: '', hover: '' };
  if (key === 'SENT_LINK_EXPIRED' || key === 'REOPENED_LINK_EXPIRED') {
    return {
      status: 'Expired',
      hover: raw?.link_expires_at ? `Expired ${dateShort(raw.link_expires_at)}` : ''
    };
  }
  if (key === 'REOPENED' && (raw?.is_link_open === false || !raw?.sent_at)) {
    return { status: 'Not sent', hover: '' };
  }
  return {
    status: 'Sent',
    hover: raw?.link_expires_at ? `Valid until ${dateShort(raw.link_expires_at)}` : ''
  };
};

export const dayCount = iso => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 86400000));
};

/** Hours if under a day, otherwise whole days. Returns null for missing/invalid timestamps. */
export const formatElapsed = iso => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const ms = Math.max(0, Date.now() - then);
  const hours = Math.floor(ms / 3600000);
  if (hours < 24) return { text: `${hours}h`, hours, days: 0 };
  const days = Math.floor(ms / 86400000);
  return { text: `${days}d`, hours, days };
};

/**
 * Aging for intake/detail:
 * - New / never sent → count from created_at (Reopened: reopened_at → updated_at → created_at)
 * - After send → count from sent_at (fallback created_at)
 * - Submitted → blank aging
 * - Stale only after the link has been sent (not while New / Not sent)
 */
export const agingLabels = record => {
  const state = intakeStateFromStatus(record?.scope_form_status || record?.state);
  const link = linkStatusFromRecord(record);
  const expired = link.status === 'Expired';
  // Trust the link chip. Do not keep "never sent" when link_status is already sent
  // but scope_form_status is still NEW / sent_at is missing (common after first send).
  const neverSent = link.status === 'Not sent';

  if (state === 'Submitted') {
    return {
      sinceSent: '',
      inState: '',
      stale: false,
      neverSent: false,
      agingAnchor: null,
      elapsed: null
    };
  }

  let agingAnchor;
  if (neverSent) {
    if (state === 'Reopened') {
      agingAnchor = record?.reopened_at || record?.updated_at || record?.created_at;
    } else {
      agingAnchor = record?.created_at;
    }
  } else {
    agingAnchor = record?.sent_at || record?.updated_at || record?.created_at;
  }
  const elapsed = formatElapsed(agingAnchor);
  const sinceLabel = neverSent
    ? state === 'Reopened'
      ? 'reopened'
      : 'created'
    : 'sent';
  const sinceSent = elapsed ? `${elapsed.text} since ${sinceLabel}` : '';

  const stale = !expired && !neverSent && !!elapsed && elapsed.days > 7;
  return { sinceSent, inState: '', stale, neverSent, agingAnchor, elapsed };
};

/** Build PATCH/create body field for KAM vs EAM from roster option roleTag. */
export const accountOwnerFieldBody = (accountOwners, selectedId) => {
  if (!selectedId) return {};
  const opt = (accountOwners || []).find(o => String(o.value) === String(selectedId));
  if (opt?.roleTag === 'EAM') return { eam_id: selectedId };
  return { kam_id: selectedId };
};

export const mapRecordListRow = raw => {
  const state = intakeStateFromStatus(raw.scope_form_status || raw.state);
  const link = linkStatusFromRecord(raw);
  const { sinceSent, inState, stale, agingAnchor } = agingLabels(raw);
  const trades = Array.isArray(raw.trades_in_scope) ? raw.trades_in_scope : [];
  const ccEmails = Array.isArray(raw.cc_emails) ? raw.cc_emails : [];
  const cc = ccEmails.length || raw.cc_count || 0;
  const recordType = raw.record_type || (raw.is_pilot === false ? 'existing_customer' : 'pilot');
  const isPilot = raw.is_pilot !== false && recordType !== 'existing_customer';
  const versionNum = raw.current_version?.version;
  const versionActor =
    raw.current_version?.actor_name ||
    raw.current_version?.actor_email ||
    raw.current_version?.filled_by?.name ||
    'internal';
  let versionLabel = '';
  if (raw.current_version) {
    if (raw.current_version.customer_submission === false) {
      versionLabel = `Filled on behalf by ${versionActor}`;
    } else {
      versionLabel = 'Customer submission (via link)';
    }
  }
  const versionDate = dateTimeShort(raw.current_version?.created_at || raw.submitted_at);

  const linkedBy = raw.linked_by?.name || raw.adopted_by?.name || '';
  const linkedAt = dateShort(raw.linked_at || raw.adopted_at);
  const linkedVerb = isPilot ? 'linked by' : 'adopted by';
  const linkedOrAdopted = (() => {
    if (!raw.company_id && !raw.linked_company?.id && !linkedBy) return '—';
    if (linkedBy && linkedAt) return `${linkedVerb} ${linkedBy} · ${linkedAt}`;
    if (linkedBy) return `${linkedVerb} ${linkedBy}`;
    if (linkedAt) return `${isPilot ? 'linked' : 'adopted'} · ${linkedAt}`;
    return isPilot ? 'Linked' : raw.outcome === 'live' ? 'Adopted' : '—';
  })();
  const linkedPrimary = (() => {
    if (!raw.company_id && !raw.linked_company?.id && !linkedBy) return '';
    if (linkedBy) return `${linkedVerb} ${linkedBy}`;
    if (linkedAt) return isPilot ? 'linked' : 'adopted';
    return isPilot ? 'Linked' : raw.outcome === 'live' ? 'Adopted' : '';
  })();

  const personRole = (person, fallback) => {
    if (!person) return fallback;
    const rawRole = person.role || person.subrole || person.role_tag || person.designation || '';
    const normalized = String(rawRole).toUpperCase();
    if (normalized.includes('EAM') || normalized.includes('ENTERPRISE')) return 'EAM';
    if (normalized.includes('KAM') || normalized.includes('KEY_ACCOUNT')) return 'KAM';
    if (normalized.includes('TCSM') || normalized.includes('TECHNICAL')) return 'TCSM';
    if (normalized.includes('FDE') || normalized === 'SE' || normalized.includes('SALES_ENGINEER')) return 'FDE';
    if (normalized.includes('AE') || normalized.includes('ACCOUNT_EXECUTIVE')) return 'AE';
    return fallback;
  };

  let accountOwnerName = '—';
  let accountOwnerRole = '';
  let technicalOwnerName = '—';
  let technicalOwnerRole = '';

  if (isPilot) {
    accountOwnerName = raw.account_executive?.name || '—';
    accountOwnerRole = accountOwnerName !== '—' ? personRole(raw.account_executive, 'AE') : '';
    technicalOwnerName = raw.fde?.name || raw.sales_engineer?.name || '—';
    technicalOwnerRole = technicalOwnerName !== '—' ? personRole(raw.fde || raw.sales_engineer, 'FDE') : '';
  } else {
    if (raw.kam?.name) {
      accountOwnerName = raw.kam.name;
      accountOwnerRole = personRole(raw.kam, 'KAM');
    } else if (raw.eam?.name || raw.enterprise_account_manager?.name) {
      accountOwnerName = raw.eam?.name || raw.enterprise_account_manager.name;
      accountOwnerRole = personRole(raw.eam || raw.enterprise_account_manager, 'EAM');
    } else if (raw.account_owner?.name) {
      accountOwnerName = raw.account_owner.name;
      accountOwnerRole = personRole(raw.account_owner, 'KAM');
    }
    technicalOwnerName = raw.technical_csm?.name || raw.technical_owner?.name || '—';
    technicalOwnerRole =
      technicalOwnerName !== '—' ? personRole(raw.technical_csm || raw.technical_owner, 'TCSM') : '';
  }

  const linkedCompanyName = raw.linked_company?.name || '';
  const adminEmail =
    raw.admin_email || raw.linked_company?.admin_email || raw.linked_company?.adminEmail || '';
  const intakeCompanyName = raw.company_name || raw.prospect_company_name || '';
  const companyName = linkedCompanyName || intakeCompanyName || '—';
  const showIntakeName =
    !!intakeCompanyName &&
    (!!linkedCompanyName
      ? intakeCompanyName.trim().toLowerCase() !== linkedCompanyName.trim().toLowerCase()
      : false);

  return {
    id: raw.id,
    linked: !!(raw.company_id || raw.linked_company?.id),
    companyId: raw.company_id || raw.linked_company?.id || null,
    email: raw.customer_email,
    company: companyName,
    linkedCompany: linkedCompanyName || companyName,
    adminEmail,
    intakeName: showIntakeName ? intakeCompanyName : '',
    prospect: isPilot && showIntakeName ? intakeCompanyName : '',
    state,
    recordType,
    recordTypeLabel: recordTypeLabel(recordType),
    isPilot,
    outcome: raw.outcome || '',
    outcomeLabel: formatOutcomeLabel({
      outcome: raw.outcome,
      isPilot,
      pilotStatus: raw.outcome
    }),
    kam: raw.kam?.name || raw.account_owner?.name || '—',
    technicalCsm: raw.technical_csm?.name || raw.technical_owner?.name || '—',
    accountOwner: accountOwnerName,
    accountOwnerRole,
    technicalOwner: technicalOwnerName,
    technicalOwnerRole,
    emailsEnabled: raw.send_email !== false && raw.send_emails !== false,
    linkStatus: link.status,
    linkHover: link.hover,
    linkExpiresAt: dateShort(raw.link_expires_at),
    trades: trades.length,
    tradeList: trades.map(t => t.name),
    creator: raw.created_by?.name || '—',
    ae: raw.account_executive?.name || '—',
    aeId: raw.account_executive?.id || null,
    fde: raw.fde?.name || raw.sales_engineer?.name || '—',
    cc,
    ccEmails,
    created: dateTimeShort(raw.created_at),
    createdAt: raw.created_at || null,
    submitted: dateTimeShort(raw.current_version?.created_at || raw.submitted_at),
    submittedAt: raw.current_version?.created_at || raw.submitted_at || null,
    updated: dateTimeShort(
      raw.updated_at || raw.current_version?.created_at || raw.submitted_at || raw.created_at
    ),
    updatedAt: raw.updated_at || raw.current_version?.created_at || raw.submitted_at || raw.created_at || null,
    sinceSent,
    inState,
    stale,
    agingAnchor: agingAnchor || null,
    pilot:
      raw.outcome === 'open'
        ? 'Pilot · open'
        : raw.outcome === 'closed_won'
          ? 'Pilot · closed won'
          : raw.outcome === 'closed_lost'
            ? 'Pilot · closed lost'
            : '—',
    pilotStatus: raw.outcome || '',
    formClosed: !!raw.form_closed,
    version: raw.current_version
      ? {
          id: raw.current_version.version_id,
          version: versionNum,
          actor: raw.current_version.actor_email,
          label: versionLabel,
          date: versionDate,
          display: versionNum != null ? `v${versionNum}${versionLabel ? ` · ${versionLabel}` : ''}` : '—'
        }
      : null,
    linkedOrAdopted,
    linkedPrimary,
    linkedBy,
    linkedAt,
    submittedEmail:
      raw.submitted_by_email && raw.submitted_by_email !== raw.customer_email ? raw.submitted_by_email : ''
  };
};

export const mapSalesTeam = payload => {
  const toOpts = (list, roleTag) =>
    (list || []).map(u => ({
      label: roleTag ? `${u.name} · ${roleTag}` : u.name,
      // String ids so Select (strict ===) matches company prefill values
      value: u.id == null ? '' : String(u.id),
      email: u.email,
      name: u.name,
      roleTag
    }));

  const ae = toOpts(payload?.AE, 'AE');
  const se = toOpts(payload?.SE, 'FDE');
  const kam = toOpts(payload?.KEY_ACCOUNT_MANAGER || payload?.KAM, 'KAM');
  const eam = toOpts(payload?.ENTERPRISE_ACCOUNT_MANAGER || payload?.EAM, 'EAM');
  const tcsm = toOpts(payload?.TECHNICAL_CSM || payload?.TCSM, 'TCSM');

  return {
    ae,
    se,
    kam,
    eam,
    tcsm,
    accountOwners: [...kam, ...eam],
    technicalOwners: tcsm,
    allRoster: [...ae, ...se, ...kam, ...eam, ...tcsm]
  };
};

const modifierKindLabel = type => {
  if (type === 'multi_select') return 'pick any';
  if (type === 'dropdown') return 'pick one';
  if (type === 'subjective') return 'text input';
  if (type === 'example') return 'examples';
  return type || '';
};

const mapAnswerOption = (o, modifierDefault) => {
  if (o && typeof o === 'object') {
    return {
      label: o.label || o.value || o.name || '',
      isDefault: !!o.default
    };
  }
  const label = String(o ?? '');
  return {
    label,
    isDefault: modifierDefault != null && String(modifierDefault) === label
  };
};

const mapModifier = m => ({
  id: m.id,
  title: m.title || '',
  body: m.body || '',
  hasBody: !!m.body,
  advanced: m.complexity === 'advanced',
  kindLabel: modifierKindLabel(m.modifier_type),
  modifierType: m.modifier_type,
  isInput: m.modifier_type === 'subjective',
  isExample: m.modifier_type === 'example',
  urls: Array.isArray(m.urls) ? m.urls : [],
  default: m.default ?? null,
  options: (m.answer_options || []).map(o => mapAnswerOption(o, m.default))
});

const mapItem = scope => ({
  id: scope.scope_id,
  label: scope.scope_name,
  advanced: scope.complexity === 'advanced',
  scopeType: scope.scope_type || '',
  isAdditional: scope.scope_type === 'additional',
  isDefault: !!scope.is_default_selected,
  priceWeight: typeof scope.price_weight === 'number' ? scope.price_weight : null,
  description: scope.scope_description || '',
  link: scope.scope_link || '',
  images: Array.isArray(scope.image_urls) ? scope.image_urls : [],
  elements: (scope.elements || []).map(e => ({
    id: e.id,
    label: e.name,
    advanced: e.complexity === 'advanced'
  })),
  modifiers: (scope.modifiers || []).map(mapModifier),
  // summary:
  //   (scope.modifiers || []).length > 0
  //     ? (scope.modifiers || []).map(m => m.title).filter(Boolean).join(' · ')
  //     : 'measured as drawn'
});

const itemMatchesQuery = (item, q) => {
  if (!q) return true;
  if ((item.label || '').toLowerCase().includes(q)) return true;
  if (item.elements.some(e => (e.label || '').toLowerCase().includes(q))) return true;
  return item.modifiers.some(m => {
    if ((m.title || '').toLowerCase().includes(q)) return true;
    return m.options.some(o => (o.label || '').toLowerCase().includes(q));
  });
};

export const catalogToTradeFullView = (payload, { query = '' } = {}) => {
  const trades = payload?.trades || [];
  const q = query.trim().toLowerCase();
  return trades
    .map(t => {
      const sections = (t.sections || []).map(s => {
        const items = (s.scopes || []).filter(scopeHasSelectableElements).map(mapItem);
        const matches = q.length >= 2 ? items.filter(i => itemMatchesQuery(i, q)) : items;
        return {
          id: s.section_id,
          name: s.section_name,
          items: matches,
          totalItems: items.length
        };
      });
      const visibleSections = sections.filter(s => s.items.length);
      const totalItems = visibleSections.reduce((a, s) => a + s.totalItems, 0);
      const hitCount = visibleSections.reduce((a, s) => a + s.items.length, 0);
      return {
        id: t.trade_id,
        name: t.trade_name,
        description: t.trade_description || '',
        sections: visibleSections,
        totalItems,
        totalSections: visibleSections.length,
        hitCount: q.length >= 2 ? hitCount : 0,
        basePricePerSheet: typeof t.base_price_per_sheet === 'number' ? t.base_price_per_sheet : null,
        maxBasicPricePerSheet:
          typeof t.max_basic_price_per_sheet === 'number' ? t.max_basic_price_per_sheet : null,
        maxAdvancedPricePerSheet:
          typeof t.max_advanced_price_per_sheet === 'number' ? t.max_advanced_price_per_sheet : null,
        pricingCurveFactor: typeof t.pricing_curve_factor === 'number' ? t.pricing_curve_factor : null
      };
    })
    .filter(t => t.sections.length > 0);
};

export const catalogVolumeDiscount = payload => {
  const raw = payload?.volume_discount;
  if (!raw || typeof raw !== 'object') return [];
  return Object.entries(raw)
    .map(([minSheet, discount]) => ({ minSheet: Number(minSheet), discount: Number(discount) }))
    .filter(t => Number.isFinite(t.minSheet) && Number.isFinite(t.discount))
    .sort((a, b) => a.minSheet - b.minSheet);
};

export const catalogIsStaffView = payload => Array.isArray(payload?.trades) && !!payload?.volume_discount;

export const catalogToTradeCards = payload => {
  const trades = payload?.trades || [];
  return trades
    .map(t => {
      let itemCount = 0;
      let elementCount = 0;
      let sectionCount = 0;
      (t.sections || []).forEach(s => {
        const scopes = (s.scopes || []).filter(scopeHasSelectableElements);
        if (!scopes.length) return;
        sectionCount += 1;
        scopes.forEach(sc => {
          itemCount += 1;
          elementCount += (sc.elements || []).length;
        });
      });
      return {
        id: t.trade_id,
        name: t.trade_name,
        description: t.trade_description || '',
        sections: sectionCount,
        items: itemCount,
        elements: elementCount
      };
    })
    .filter(t => t.sections > 0);
};

// Flatten a single trade (from GET /api/dashboard/get_scopes/) into on-behalf items —
// scope-level rows the internal user can tick to fill out the customer form.
export const catalogTradeToItems = (catalogPayload, tradeId) => {
  const trade = (catalogPayload?.trades || []).find(t => t.trade_id === tradeId);
  if (!trade) return [];
  const rows = [];
  (trade.sections || []).forEach(section => {
    (section.scopes || []).forEach(scope => {
      const elementCount = (scope.elements || []).length;
      const modifierCount = (scope.modifiers || []).length;
      rows.push({
        id: scope.scope_id,
        label: scope.scope_name,
        sectionName: section.section_name,
        advanced: scope.complexity === 'advanced',
        subline: `${elementCount} elements · ${
          modifierCount ? `${modifierCount} question${modifierCount > 1 ? 's' : ''}` : 'no questions'
        }`
      });
    });
  });
  return rows.slice(0, 8);
};

export const parseFieldErrors = errorPayload => {
  if (!errorPayload) return {};
  if (typeof errorPayload === 'string') return { _detail: errorPayload };
  if (errorPayload.detail) return { _detail: errorPayload.detail };
  return Object.entries(errorPayload).reduce((a, [k, v]) => {
    a[k] = Array.isArray(v) ? v.join(', ') : String(v);
    return a;
  }, {});
};
